import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { sendOrderPurchaseData } from "../../../apis/apis.logistics/apisProduct";
import swal from "sweetalert2";

const inputClass =
    "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";

export function NewOrderPurchaseOperationDetailList({
    order,
    setOrder,
    initialOrderState,
}) {
    const navigate = useNavigate();

    const sendOrderPurchase = async () => {
        try {
            const cleanedOrder = {
                ...order,
                affectsStock:
                    order.affectsStock === "true" ||
                    order.affectsStock === true,
                operationItems: order.operationItems.map((item) => {
                    const { tariffs, ...cleanItem } = item;
                    return cleanItem;
                }),
            };
            const response = await sendOrderPurchaseData(cleanedOrder);
            return response;
        } catch (error) {
            throw error;
        }
    };

    function handleRemoveItem(index) {
        swal.fire({
            title: "¿Está usted seguro?",
            text: "¿Eliminar el item?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Sí, eliminar",
            cancelButtonText: "Cancelar",
        }).then((result) => {
            if (result.isConfirmed) {
                setOrder({
                    ...order,
                    operationItems: order.operationItems.filter(
                        (obj, idx) => idx !== index,
                    ),
                });
            }
        });
    }

    const validateOperationItems = (items) => {
        return items.every((item) => {
            return (
                (typeof item.taxableAmount === "string" ||
                    typeof item.taxableAmount === "number") &&
                (typeof item.igvAmount === "string" ||
                    typeof item.igvAmount === "number") &&
                (typeof item.totalWithIgv === "string" ||
                    typeof item.totalWithIgv === "number") &&
                !isNaN(parseFloat(item.taxableAmount)) &&
                !isNaN(parseFloat(item.igvAmount)) &&
                !isNaN(parseFloat(item.totalWithIgv))
            );
        });
    };

    const validateTotals = (order) => {
        const sumTaxable = order.operationItems.reduce(
            (sum, item) => sum + parseFloat(item.taxableAmount),
            0,
        );
        const sumIgv = order.operationItems.reduce(
            (sum, item) => sum + parseFloat(item.igvAmount),
            0,
        );
        const sumTotal = order.operationItems.reduce(
            (sum, item) => sum + parseFloat(item.totalWithIgv),
            0,
        );

        return true;
    };

    function generateTotals() {
        // Los items gratuitos declaran un valor de referencia (taxableAmount/igvAmount/totalWithIgv)
        // pero no se cobran: se excluyen de los totales "a pagar" y van al bucket "Total Gratuito",
        // igual que "Operaciones Gravadas" vs "Operaciones Gratuitas" en la factura del proveedor.
        const totalTaxable = order.operationItems.reduce(
            (sum, item) =>
                sum + (item.isFree ? 0 : parseFloat(item.taxableAmount || 0)),
            0,
        );
        const totalIgv = order.operationItems.reduce(
            (sum, item) =>
                sum + (item.isFree ? 0 : parseFloat(item.igvAmount || 0)),
            0,
        );
        const totalPayable = order.operationItems.reduce(
            (sum, item) =>
                sum + (item.isFree ? 0 : parseFloat(item.totalWithIgv || 0)),
            0,
        );
        const totalFree = order.operationItems.reduce(
            (sum, item) => sum + parseFloat(item.freeAmount || 0),
            0,
        );
        const totalFreeValue = order.operationItems.reduce(
            (sum, item) =>
                sum +
                parseFloat(item.freeAmount || 0) +
                parseFloat(item.freeIgvAmount || 0),
            0,
        );

        setOrder({
            ...order,
            totalTaxable: totalTaxable.toFixed(2),
            totalIgv: totalIgv.toFixed(2),
            totalPayable: totalPayable.toFixed(2),
            totalFree: totalFree.toFixed(2),
            totalFreeValue: totalFreeValue.toFixed(2),
            operationNumberItems: order.operationItems.length,
        });
    }

    const handleClickBtnProcess = async () => {
        if (order.operationItems.length) {
            if (order.supplierDocumentNumber != "") {
                if (order.supplierName != "") {
                    if (validateOperationItems(order.operationItems)) {
                        if (validateTotals(order)) {
                            await sendOrderPurchase()
                                .then((r) => {
                                    setOrder({
                                        ...order,
                                        operationID: r.operationID,
                                        operationCorrelative:
                                            r.operationCorrelative,
                                    });

                                    return r.status;
                                })
                                .then((status) => {
                                    if (status == 201) {
                                        toast.success(
                                            "Operacion registrada con exito!",
                                        );
                                        setOrder(initialOrderState);
                                        navigate(
                                            "/buys/purchase_orders_issued",
                                        );
                                    } else {
                                        toast.error("Ocurrio algun error");
                                    }
                                });
                        } else {
                            toast.error("Los totales no coinciden");
                        }
                    } else {
                        toast.error("Los items tienen datos incorrectos");
                    }
                } else {
                    toast.error("Verifique el nombre del proveedor");
                }
            } else {
                toast.error("Verifique el numero de documento del proveedor");
            }
        } else {
            toast.error("Verifique los items a registrar");
        }
    };

    // Recalcula los montos de una línea (por índice) al cambiar cantidad, costo, %dcto o valor de dcto
    const recalcItemAt = (index, field, rawValue) => {
        setOrder({
            ...order,
            operationItems: order.operationItems.map((obj, idx) => {
                if (idx !== index) return obj;

                const conditionIgv = order.conditionIgv;
                let quantity = Number(obj.quantityOrdered) || 0;
                let unitPrice = Number(obj.unitPrice) || 0;
                let discountPercent = Number(obj.percentageDiscount) || 0;
                let grossAmount = Number(obj.grossAmount) || 0;
                let discountAmount = Number(obj.discountAmount) || 0;

                if (field === "quantityOrdered") {
                    quantity = Number(rawValue) || 0;
                    grossAmount = quantity * unitPrice;
                    discountAmount = (grossAmount * discountPercent) / 100;
                } else if (field === "unitPrice") {
                    unitPrice = Number(rawValue) || 0;
                    grossAmount = quantity * unitPrice;
                    discountAmount = (grossAmount * discountPercent) / 100;
                } else if (field === "percentageDiscount") {
                    discountPercent = Number(rawValue) || 0;
                    discountAmount = (grossAmount * discountPercent) / 100;
                } else if (field === "discountAmount") {
                    discountAmount = Number(rawValue) || 0;
                    discountPercent =
                        grossAmount > 0
                            ? (discountAmount / grossAmount) * 100
                            : 0;
                }

                const netAmount = grossAmount - discountAmount;
                let taxableAmount, igvAmount, totalWithIgv;
                if (conditionIgv === "01") {
                    taxableAmount = netAmount / 1.18;
                    igvAmount = netAmount - taxableAmount;
                    totalWithIgv = netAmount;
                } else {
                    taxableAmount = netAmount;
                    igvAmount = taxableAmount * 0.18;
                    totalWithIgv = netAmount + igvAmount;
                }

                return {
                    ...obj,
                    quantityOrdered:
                        field === "quantityOrdered"
                            ? rawValue
                            : obj.quantityOrdered,
                    quantity:
                        field === "quantityOrdered"
                            ? rawValue
                            : obj.quantity,
                    unitPrice:
                        field === "unitPrice" ? rawValue : obj.unitPrice,
                    percentageDiscount:
                        field === "percentageDiscount"
                            ? rawValue
                            : discountPercent.toFixed(2),
                    grossAmount: grossAmount.toFixed(2),
                    discountAmount:
                        field === "discountAmount"
                            ? rawValue
                            : discountAmount.toFixed(2),
                    netAmount: netAmount.toFixed(2),
                    taxableAmount: taxableAmount.toFixed(2),
                    igvAmount: igvAmount.toFixed(2),
                    totalWithIgv: totalWithIgv.toFixed(2),
                    freeAmount: obj.isFree ? netAmount.toFixed(2) : 0,
                };
            }),
        });
    };

    useEffect(() => {
        generateTotals();
    }, [order.operationItems]);

    return (
        <>
            {/* Desktop Table View */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden mb-4 hidden md:block">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm border-separate border-spacing-0">
                        <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0 z-10">
                            <tr>
                                <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                                    SKU
                                </th>
                                <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                                    Producto
                                </th>
                                <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                                    Unidad
                                </th>
                                <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                                    Cantidad
                                </th>
                                <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                                    Costo
                                </th>
                                <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                                    Subtotal
                                </th>
                                <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                                    % Dcto
                                </th>
                                <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 bg-cyan-50 dark:bg-cyan-900/30">
                                    Valor Dcto
                                </th>
                                <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                                    Neto
                                </th>
                                <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                                    IGV
                                </th>
                                <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                                    Tipo
                                </th>
                                <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                                    Elim
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {order.operationItems.map((item, index) => {
                                return (
                                    <tr
                                        key={index}
                                        className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${item.isFree ? "bg-amber-50/60 dark:bg-amber-900/10" : ""}`}
                                    >
                                        <td className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle text-center">
                                            {item.sku}
                                        </td>
                                        <td className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle text-left">
                                            {item.productName}
                                            <br />
                                            <span className="text-gray-500 dark:text-gray-400 text-xs">
                                                Fec. Vto: {item.expireDate}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle text-center">
                                            {item.unitName}
                                        </td>
                                        <td className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle">
                                            <input
                                                type="number"
                                                className={
                                                    inputClass + " text-center"
                                                }
                                                value={item.quantityOrdered}
                                                onWheel={(e) => e.target.blur()}
                                                onChange={(e) => {
                                                    if (
                                                        Number(
                                                            e.target.value,
                                                        ) >= 0 &&
                                                        Number(
                                                            e.target.value,
                                                        ) <= 100000
                                                    ) {
                                                        recalcItemAt(
                                                            index,
                                                            "quantityOrdered",
                                                            e.target.value,
                                                        );
                                                    }
                                                }}
                                                maxLength={6}
                                                autoComplete="off"
                                            />
                                        </td>
                                        <td className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle">
                                            <input
                                                type="number"
                                                className={
                                                    inputClass + " text-center"
                                                }
                                                value={item.unitPrice}
                                                onWheel={(e) => e.target.blur()}
                                                onChange={(e) => {
                                                    if (
                                                        Number(
                                                            e.target.value,
                                                        ) >= 0 &&
                                                        Number(
                                                            e.target.value,
                                                        ) <= 100000
                                                    ) {
                                                        recalcItemAt(
                                                            index,
                                                            "unitPrice",
                                                            e.target.value,
                                                        );
                                                    }
                                                }}
                                                maxLength={6}
                                                autoComplete="off"
                                            />
                                        </td>
                                        <td className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle text-right font-medium">
                                            S/ {item.grossAmount}
                                        </td>
                                        <td className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle">
                                            <input
                                                type="number"
                                                className={
                                                    inputClass + " text-center"
                                                }
                                                value={item.percentageDiscount}
                                                onWheel={(e) => e.target.blur()}
                                                onChange={(e) => {
                                                    if (
                                                        Number(
                                                            e.target.value,
                                                        ) >= 0 &&
                                                        Number(
                                                            e.target.value,
                                                        ) <= 100
                                                    ) {
                                                        recalcItemAt(
                                                            index,
                                                            "percentageDiscount",
                                                            e.target.value,
                                                        );
                                                    }
                                                }}
                                                maxLength={6}
                                                autoComplete="off"
                                            />
                                        </td>
                                        <td className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle bg-cyan-50 dark:bg-cyan-900/30">
                                            <input
                                                type="number"
                                                className={
                                                    inputClass + " text-center"
                                                }
                                                value={item.discountAmount}
                                                onWheel={(e) => e.target.blur()}
                                                onChange={(e) => {
                                                    recalcItemAt(
                                                        index,
                                                        "discountAmount",
                                                        e.target.value,
                                                    );
                                                }}
                                                maxLength={6}
                                                autoComplete="off"
                                            />
                                        </td>
                                        <td className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle text-right font-bold">
                                            S/ {item.taxableAmount}
                                        </td>
                                        <td className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle text-center">
                                            <span
                                                className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                                    order.conditionIgv === "01"
                                                        ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                                                        : "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                                                }`}
                                            >
                                                {order.conditionIgv === "01"
                                                    ? "Incl"
                                                    : "Apar"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle text-center">
                                            {item.isFree ? (
                                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                                    <i className="fas fa-gift mr-1"></i>
                                                    Gratis
                                                </span>
                                            ) : (
                                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                                    Gravado
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle text-center">
                                            <button
                                                type="button"
                                                className="inline-flex items-center gap-1 rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs font-medium px-2 py-1 transition-colors"
                                                onClick={() =>
                                                    handleRemoveItem(index)
                                                }
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot className="bg-gray-100 dark:bg-gray-700 font-bold">
                            <tr>
                                <td
                                    colSpan={9}
                                    className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-200 dark:border-gray-600 align-middle text-right"
                                >
                                    Subtotal (Gravado)
                                </td>
                                <td
                                    className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-200 dark:border-gray-600 align-middle text-right text-blue-700 dark:text-blue-300"
                                    id="totalTaxable"
                                >
                                    S/ {order.totalTaxable}
                                </td>
                                <td className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-200 dark:border-gray-600 align-middle text-right"></td>
                            </tr>
                            <tr>
                                <td
                                    colSpan={9}
                                    className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-200 dark:border-gray-600 align-middle text-right"
                                >
                                    I.G.V. 18%
                                </td>
                                <td
                                    className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-200 dark:border-gray-600 align-middle text-right text-blue-700 dark:text-blue-300"
                                    id="totalIgv"
                                >
                                    S/ {order.totalIgv}
                                </td>
                                <td className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-200 dark:border-gray-600 align-middle text-right"></td>
                            </tr>
                            {Number(order.totalFree) > 0 && (
                                <tr>
                                    <td
                                        colSpan={9}
                                        className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-200 dark:border-gray-600 align-middle text-right text-amber-700 dark:text-amber-400"
                                    >
                                        <i className="fas fa-gift mr-1"></i>
                                        Total Gratuito (no se cobra)
                                    </td>
                                    <td
                                        className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-200 dark:border-gray-600 align-middle text-right text-amber-700 dark:text-amber-400"
                                        id="totalFree"
                                    >
                                        S/ {order.totalFree}
                                    </td>
                                    <td className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-200 dark:border-gray-600 align-middle text-right"></td>
                                </tr>
                            )}
                            <tr style={{ fontSize: "1.05rem" }}>
                                <td
                                    colSpan={9}
                                    className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-200 dark:border-gray-600 align-middle text-right"
                                >
                                    Total a Pagar
                                </td>
                                <td
                                    className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-200 dark:border-gray-600 align-middle text-right text-blue-700 dark:text-blue-300"
                                    id="totalPayable"
                                >
                                    S/ {order.totalPayable}
                                </td>
                                <td className="px-3 py-2 text-xs whitespace-nowrap border-b border-gray-200 dark:border-gray-600 align-middle text-right"></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4 mb-4">
                {order.operationItems.map((item, index) => {
                    return (
                        <div
                            key={index}
                            className={`rounded-xl border bg-white dark:bg-gray-800 shadow-sm p-4 ${item.isFree ? "border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-900/10" : "border-gray-200 dark:border-gray-700"}`}
                        >
                            <div className="flex justify-between items-center mb-3">
                                <div>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                        {item.productName}
                                        {item.isFree && (
                                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                                <i className="fas fa-gift mr-1"></i>
                                                Gratis
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {item.sku} · {item.unitName}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="inline-flex items-center gap-1 rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs font-medium px-2 py-1 transition-colors"
                                    onClick={() => handleRemoveItem(index)}
                                >
                                    <i className="fas fa-trash"></i>
                                </button>
                            </div>

                            <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-1.5">
                                    <label className="text-gray-500 dark:text-gray-400">
                                        Cantidad
                                    </label>
                                    <input
                                        type="number"
                                        className={
                                            inputClass + " text-center mt-1"
                                        }
                                        value={item.quantityOrdered}
                                        onWheel={(e) => e.target.blur()}
                                        onChange={(e) => {
                                            if (
                                                Number(e.target.value) >= 0 &&
                                                Number(e.target.value) <= 100000
                                            ) {
                                                recalcItemAt(
                                                    index,
                                                    "quantityOrdered",
                                                    e.target.value,
                                                );
                                            }
                                        }}
                                        maxLength={6}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-1.5">
                                    <label className="text-gray-500 dark:text-gray-400">
                                        Costo
                                    </label>
                                    <input
                                        type="number"
                                        className={
                                            inputClass + " text-center mt-1"
                                        }
                                        value={item.unitPrice}
                                        onWheel={(e) => e.target.blur()}
                                        onChange={(e) => {
                                            if (
                                                Number(e.target.value) >= 0 &&
                                                Number(e.target.value) <= 100000
                                            ) {
                                                recalcItemAt(
                                                    index,
                                                    "unitPrice",
                                                    e.target.value,
                                                );
                                            }
                                        }}
                                        maxLength={6}
                                        autoComplete="off"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-1.5">
                                    <p className="text-gray-500 dark:text-gray-400">
                                        Subtotal
                                    </p>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300">
                                        S/ {item.grossAmount}
                                    </p>
                                </div>
                                <div className="bg-cyan-50 dark:bg-cyan-900/30 rounded-lg px-3 py-1.5">
                                    <p className="text-gray-500 dark:text-gray-400">
                                        % Dcto
                                    </p>
                                    <input
                                        type="number"
                                        className={
                                            inputClass + " text-center mt-1"
                                        }
                                        value={item.percentageDiscount}
                                        onWheel={(e) => e.target.blur()}
                                        onChange={(e) => {
                                            if (
                                                Number(e.target.value) >= 0 &&
                                                Number(e.target.value) <= 100
                                            ) {
                                                recalcItemAt(
                                                    index,
                                                    "percentageDiscount",
                                                    e.target.value,
                                                );
                                            }
                                        }}
                                        maxLength={6}
                                        autoComplete="off"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 text-xs">
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-1.5">
                                    <p className="text-gray-500 dark:text-gray-400">
                                        Valor Dcto
                                    </p>
                                    <input
                                        type="number"
                                        className={
                                            inputClass + " text-center mt-1"
                                        }
                                        value={item.discountAmount}
                                        onWheel={(e) => e.target.blur()}
                                        onChange={(e) => {
                                            recalcItemAt(
                                                index,
                                                "discountAmount",
                                                e.target.value,
                                            );
                                        }}
                                        maxLength={6}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-1.5">
                                    <p className="text-gray-500 dark:text-gray-400">
                                        Neto
                                    </p>
                                    <p className="font-semibold text-gray-700 dark:text-gray-300">
                                        S/ {item.taxableAmount}
                                    </p>
                                </div>
                            </div>

                            <div className="mt-3">
                                <span
                                    className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                                        item.conditionIgv === "01"
                                            ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                                            : "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                                    }`}
                                >
                                    IGV{" "}
                                    {item.conditionIgv === "01"
                                        ? "Incluido"
                                        : "Aparte"}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Total Summary on Mobile */}
            {order.operationItems.length > 0 && (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm p-4 mb-4 md:hidden">
                    <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">
                                Subtotal (Gravado)
                            </span>
                            <span className="font-semibold text-gray-900 dark:text-white">
                                S/ {order.totalTaxable}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500 dark:text-gray-400">
                                I.G.V. 18%
                            </span>
                            <span className="font-semibold text-gray-900 dark:text-white">
                                S/ {order.totalIgv}
                            </span>
                        </div>
                        {Number(order.totalFree) > 0 && (
                            <div className="flex justify-between">
                                <span className="text-amber-700 dark:text-amber-400">
                                    <i className="fas fa-gift mr-1"></i>
                                    Total Gratuito (no se cobra)
                                </span>
                                <span className="font-semibold text-amber-700 dark:text-amber-400">
                                    S/ {order.totalFree}
                                </span>
                            </div>
                        )}
                        <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-2">
                            <span className="font-bold text-gray-900 dark:text-white">
                                Total a Pagar
                            </span>
                            <span className="font-bold text-blue-700 dark:text-blue-300">
                                S/ {order.totalPayable}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between items-center flex-wrap gap-3">
                <div className="flex flex-wrap gap-2">
                    <Link
                        to="/buys/purchase_orders_issued"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-sm font-medium px-4 py-2 transition-colors"
                    >
                        <i className="fas fa-list mr-2"></i>
                        Ver Órdenes
                    </Link>
                </div>
                <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-medium px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={handleClickBtnProcess}
                >
                    <i className="fas fa-check-circle mr-2"></i>
                    Procesar Orden
                </button>
            </div>
        </>
    );
}
